import React, { useCallback, useState } from 'react';

import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import { IconButton } from '@atlaskit/button/new';
import { cssMap, cx } from '@atlaskit/css';
import DropdownMenu, {
	DropdownItem,
	DropdownItemGroup,
	type OnOpenChangeArgs,
} from '@atlaskit/dropdown-menu';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import { fg } from '@atlaskit/platform-feature-flags';
import { Popup } from '@atlaskit/popup/popup';
import { Box, Inline, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import { isAgentCreatorType } from '@atlaskit/rovo-agent-components/common/utils/is-agent-creator-type';
import { isForgeAgentByCreatorType } from '@atlaskit/rovo-agent-components/common/utils/is-forge-agent';
import { AgentAvatar } from '@atlaskit/rovo-agent-components/ui/AgentAvatar';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import TooltipPrimitive from '@atlaskit/tooltip/TooltipPrimitive';
import VisuallyHidden from '@atlaskit/visually-hidden/visually-hidden';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';

import type { Conversation } from '../../../types';
import { getConversationListRowLoadingStatus } from '../../../utils/conversation-task-status-indicator';
import { useUpdateTime } from '../../chat-conversation-list/chat-item/use-update-time';
import {
	ConversationListRowStatusChip,
	ConversationStatusLabel,
} from '../../conversation-status-label';
import { useConversationModal } from '../../hooks/conversation-modal';
import { ScopeFileBlobPreviewModal } from '../scope-file-blob-preview-modal';
import { ScopeRow } from '../scope-row';

import messages from './messages';

const MAX_VISIBLE_SCOPE_ICONS = 3;

const styles = cssMap({
	wrapper: {
		display: 'flex',
		justifyContent: 'end',
		position: 'relative',

		borderRadius: token('radius.small'),
		paddingBlock: token('space.150'),
		paddingInline: token('space.200'),

		cursor: 'pointer',
	},

	wrapperHoverBackground: {
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
		},
	},

	wrapperParentHoverBackground: {
		paddingInlineStart: token('space.250'),
	},

	pressableContainer: {
		position: 'absolute',
		inset: '0',
		zIndex: 0,
		backgroundColor: 'transparent',
		paddingInline: token('space.0'),
	},
	visualContent: {
		position: 'relative',
		zIndex: 1,
		flexGrow: 1,
		// Keep visual content above the full-row button without intercepting its pointer events.
		pointerEvents: 'none',
	},

	inlineContainer: {
		gap: token('space.150'),
		minWidth: '0px',
		flex: '1',
	},

	contentContainer: {
		display: 'flex',
		flexDirection: 'column',
		boxSizing: 'border-box',
		flexGrow: 1,
		minWidth: '0px',
		gap: token('space.050'),
	},

	hidden: {
		opacity: '0',
	},

	footerInline: {
		minWidth: '0px',
		width: '100%',
	},
	timestamp: {
		flexShrink: 0,
		whiteSpace: 'nowrap',
	},

	controlsContainer: {
		position: 'relative',
		zIndex: 100,
		display: 'flex',
		alignItems: 'center',
		flexShrink: 0,
	},
	pinnedLabel: {
		flexShrink: 0,
	},
	statusContainer: {
		position: 'absolute',
		inset: '0',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-end',
		marginRight: token('space.100'),
	},
	contextMenuContainer: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.025'),
		flexShrink: 0,
	},
	/** Wraps the full first-scope row (name + overflow chip) so the tooltip trigger covers both. */
	firstScopeTooltipTrigger: {
		position: 'relative',
		zIndex: 1,
		display: 'inline-flex',
		alignItems: 'center',
		maxWidth: '100%',
		minWidth: '0px',
		pointerEvents: 'auto',
	},
	firstScopeTooltipClickableTrigger: {
		cursor: 'pointer',
	},
	scopeTooltipContent: {
		boxSizing: 'border-box',
		maxWidth: '240px',
		backgroundColor: token('color.background.neutral.bold'),
		borderRadius: token('radius.small'),
		color: token('color.text.inverse'),
		font: token('font.body.small'),
		overflowWrap: 'break-word',
		paddingBlock: token('space.050'),
		paddingInline: token('space.075'),
		wordWrap: 'break-word',
	},

	scopeOverflowCount: {
		flexShrink: '0',
		whiteSpace: 'nowrap',
		marginInlineStart: token('space.negative.025'),
	},

	scopeNameWrapper: {
		minWidth: '0px',
		overflow: 'hidden',
	},
	scopeNameClickableWrapper: {
		'&:hover': {
			textDecoration: 'underline',
		},
	},
});

export type ChatItemProps = {
	conversation: Conversation;
	onConversationSelect: (conversationId: string) => void;
	onConversationPinToggle?: (conversationId: string, isPinned: boolean) => void | Promise<void>;
	isPinnedChatsEnabled?: boolean;
	isSpinnerOverlayConversationIconEnabled: boolean;
	isSelected?: boolean;
	/** Cloud / site id for scope file preview fetches (`x-cloudid`). */
	cloudId?: string;
	/** Hide history-specific row controls, such as status chip and context menu. */
	hideActions?: boolean;
	/** True when the parent row handles hover and active backgrounds. */
	hasParentHoverBackground?: boolean;
	/** Accessible label for the row button. */
	ariaLabel?: string;
	/** Test id applied to the row button. */
	testId?: string;
	/** Optional leading content for the footer metadata row. */
	footerPrefix?: React.ReactNode;
};

const FooterSeparator = ({
	color,
}: {
	color: 'color.text.subtlest' | 'color.text.subtle';
}): React.JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Box as="span" aria-hidden="true">
			<Text size="small" color={color}>
				{formatMessage(messages.footerSeparator)}
			</Text>
		</Box>
	);
};

const ScopeOverflowCount = ({ count }: { count: number }): React.JSX.Element | null => {
	const { formatMessage } = useIntl();

	if (count <= 0) {
		return null;
	}

	return (
		<React.Fragment>
			<Box as="span" aria-hidden="true" xcss={styles.scopeOverflowCount}>
				<Text as="span" size="small" weight="regular" align="start" color="color.text.subtlest">
					{formatMessage(messages.scopesOverflowCount, { count })}
				</Text>
			</Box>
			<VisuallyHidden>
				{formatMessage(messages.scopesOverflowAccessibleLabel, { count })}
			</VisuallyHidden>
		</React.Fragment>
	);
};

export const ChatItem = ({
	conversation,
	onConversationSelect,
	onConversationPinToggle,
	isPinnedChatsEnabled = false,
	cloudId,
	hideActions = false,
	hasParentHoverBackground = false,
	ariaLabel,
	testId,
	footerPrefix,
}: ChatItemProps): React.JSX.Element => {
	di(useConversationModal);
	const { formatMessage } = useIntl();
	const { openModal } = useConversationModal();
	const { sendAnalyticsEvent } = useAnalytics();
	const [isContextMenuOpen, setIsContextMenuOpen] = useState(false);
	const [isScopeTooltipOpen, setIsScopeTooltipOpen] = useState(false);
	const [isHoveredOrFocused, setIsHoveredOrFocused] = useState(false);
	const [, setSelectedConversation] = useState<string | null>(null);
	const [fileScopePreview, setFileScopePreview] = useState<{ link: string; name: string } | null>(
		null,
	);
	const lastMessageTimeDelta = useUpdateTime({
		isoDate: conversation.lastMessageTimestamp,
		relativeTimeStyle: 'long',
	});

	const showThinkingFooterRow =
		getConversationListRowLoadingStatus(conversation, conversation.readStatus === false) ===
		'loading';

	const agentName = conversation.agent?.agent_name;
	const shouldShowAgentInfo = !conversation.agent?.is_default;
	const scopes = conversation.scopes;
	const firstScope = scopes?.[0];
	const hasMultipleScopes = (scopes?.length ?? 0) > 1;
	const visibleScopes = hasMultipleScopes
		? (scopes?.slice(0, MAX_VISIBLE_SCOPE_ICONS) ?? [])
		: firstScope
			? [firstScope]
			: [];
	const scopesOverflowCount = Math.max((scopes?.length ?? 0) - visibleScopes.length, 0);
	const isClickableScopeTooltipsEnabled = fg('rovo_chat_clickable_scope_tooltips');
	const showControls = isHoveredOrFocused || isContextMenuOpen;

	const handleConversationClick = (conversation: Conversation) => {
		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'chatViewChatItemClick',
			attributes: {
				conversationId: conversation.id,
			},
		});
		if (onConversationSelect) {
			onConversationSelect(conversation.id);
		}
		setSelectedConversation(conversation.id);
	};

	const handleContextMenuToggle = useCallback((update: OnOpenChangeArgs) => {
		setIsContextMenuOpen(update.isOpen);
	}, []);

	const handleScopeTooltipClose = useCallback(() => {
		setIsScopeTooltipOpen(false);
	}, []);

	const handleScopeTooltipTriggerClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
		event.preventDefault();
		event.stopPropagation();
		setIsScopeTooltipOpen((isOpen) => !isOpen);
	}, []);

	const handleScopeTooltipTriggerKeyDown = useCallback(
		(event: React.KeyboardEvent<HTMLElement>) => {
			if (event.key === 'Enter' || event.key === ' ') {
				event.preventDefault();
				event.stopPropagation();
				setIsScopeTooltipOpen((isOpen) => !isOpen);
			}
		},
		[],
	);

	const pinButtonLabel = formatMessage(
		conversation.isPinned ? messages.chatUnpinLabel : messages.chatPinLabel,
	);

	const handlePinToggle = useCallback(() => {
		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: conversation.isPinned ? 'unpinConversationButton' : 'pinConversationButton',
		});

		void Promise.resolve(onConversationPinToggle?.(conversation.id, !conversation.isPinned)).catch(
			() => {},
		);
	}, [conversation.id, conversation.isPinned, onConversationPinToggle, sendAnalyticsEvent]);

	const handlePinMenuItemClicked = useCallback(
		(e: React.MouseEvent<Element> | React.KeyboardEvent<Element>) => {
			e.stopPropagation();
			handlePinToggle();
		},
		[handlePinToggle],
	);

	const handleScopeLinkClick = useCallback(
		(variant: 'default' | 'tooltip') => {
			sendAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'link',
				actionSubjectId: 'scopeLink',
				attributes: {
					isTooltip: variant === 'tooltip',
				},
			});
		},
		[sendAnalyticsEvent],
	);

	const handleScopeLinkHovered = useCallback(() => {
		sendAnalyticsEvent({
			action: 'hovered',
			actionSubject: 'link',
			actionSubjectId: 'scopeLink',
			attributes: {
				scopeCount: scopes?.length ?? 0,
			},
		});
	}, [sendAnalyticsEvent, scopes]);

	const footerTextColor = 'color.text.subtlest';
	const agentCreatorType = conversation.agent?.creator_type;
	const isForgeAgent =
		typeof agentCreatorType === 'string' && isAgentCreatorType(agentCreatorType)
			? isForgeAgentByCreatorType(agentCreatorType)
			: false;
	const hasFooterContentAfterPrefix =
		showThinkingFooterRow ||
		(shouldShowAgentInfo && Boolean(conversation.agent)) ||
		Boolean(firstScope) ||
		Boolean(conversation.lastMessageTimestamp) ||
		(isPinnedChatsEnabled && Boolean(conversation.isPinned));

	return (
		<Box
			xcss={cx(
				styles.wrapper,
				hasParentHoverBackground && styles.wrapperParentHoverBackground,
				!hasParentHoverBackground && styles.wrapperHoverBackground,
			)}
			onMouseEnter={() => setIsHoveredOrFocused(true)}
			onMouseLeave={() => setIsHoveredOrFocused(false)}
			onFocus={() => setIsHoveredOrFocused(true)}
			onBlur={() => setIsHoveredOrFocused(false)}
		>
			<Pressable
				xcss={styles.pressableContainer}
				key={conversation.id}
				onClick={() => handleConversationClick(conversation)}
				aria-label={ariaLabel ?? conversation.name}
				testId={testId}
			/>
			<Inline xcss={cx(styles.inlineContainer, styles.visualContent)}>
				<Box xcss={styles.contentContainer}>
					<Box>
						{/* primary line */}
						<Text size="medium" weight="regular" maxLines={1} align="start">
							{conversation.name}
						</Text>
					</Box>

					{/* footer content */}
					<Inline alignBlock="center" space="space.050" xcss={styles.footerInline}>
						{footerPrefix}
						{footerPrefix && hasFooterContentAfterPrefix && (
							<FooterSeparator color={footerTextColor} />
						)}
						{/* Active thinking replaces the usual timestamp and source metadata. */}
						{showThinkingFooterRow && <ConversationStatusLabel conversation={conversation} />}
						{!showThinkingFooterRow && conversation.lastMessageTimestamp && (
							<Box as="span" xcss={styles.timestamp}>
								<Text as="span" size="small" weight="regular" align="start" color={footerTextColor}>
									{lastMessageTimeDelta}
								</Text>
							</Box>
						)}
						{!showThinkingFooterRow && shouldShowAgentInfo && conversation.agent && (
							<React.Fragment>
								{conversation.lastMessageTimestamp && <FooterSeparator color={footerTextColor} />}
								<AgentAvatar
									agentNamedId={conversation.agent.agent_named_id}
									agentId={conversation.agent.agent_id}
									agentIdentityAccountId={conversation.agent.identity_account_id}
									size="xxsmall"
									showBorder={false}
									isForgeAgent={
										fg('rovo_agent_support_a2a_avatar')
											? isForgeAgent
											: conversation.agent.creator_type === 'THIRD_PARTY' ||
												conversation.agent.creator_type === 'FORGE'
									}
									forgeAgentIconUrl={conversation.agent.forge_url}
								/>
								<Text
									size="small"
									weight="regular"
									maxLines={1}
									align="start"
									color={footerTextColor}
								>
									{agentName}
								</Text>
							</React.Fragment>
						)}
						{!showThinkingFooterRow && firstScope && (
							<React.Fragment>
								{(conversation.lastMessageTimestamp ||
									(shouldShowAgentInfo && conversation.agent)) && (
									<FooterSeparator color={footerTextColor} />
								)}
								{isClickableScopeTooltipsEnabled ? (
									<Popup
										isOpen={isScopeTooltipOpen}
										onClose={handleScopeTooltipClose}
										placement="bottom-start"
										shouldRenderToParent
										content={() => (
											<TooltipPrimitive placement="bottom" role="tooltip">
												<Box xcss={styles.scopeTooltipContent}>
													<Stack space="space.100">
														{(scopes ?? []).map((scope, index) => (
															<Box key={`${scope.link ?? 'scope'}-${index}`}>
																<ScopeRow
																	scope={scope}
																	variant="tooltip"
																	maxLines={2}
																	onFilePreview={(link, name) =>
																		setFileScopePreview({ link, name })
																	}
																	onScopeLinkClick={handleScopeLinkClick}
																/>
															</Box>
														))}
													</Stack>
												</Box>
											</TooltipPrimitive>
										)}
										trigger={(triggerProps) => (
											<Box
												as="span"
												role="button"
												tabIndex={0}
												xcss={cx(
													styles.firstScopeTooltipTrigger,
													styles.firstScopeTooltipClickableTrigger,
												)}
												{...triggerProps}
												onClick={handleScopeTooltipTriggerClick}
												onKeyDown={handleScopeTooltipTriggerKeyDown}
											>
												<Inline alignBlock="center" space="space.050">
													{visibleScopes.map((scope, index) => (
														<Box
															key={`${scope.link ?? 'scope'}-${index}`}
															xcss={cx(
																styles.scopeNameWrapper,
																!hasMultipleScopes && styles.scopeNameClickableWrapper,
															)}
														>
															<ScopeRow
																scope={{ ...scope, link: undefined }}
																variant="default"
																maxLines={1}
																showName={!hasMultipleScopes}
																showLinkFallbackIcon
																showIconWithoutLink
																onScopeLinkHover={handleScopeLinkHovered}
															/>
														</Box>
													))}
													<ScopeOverflowCount count={scopesOverflowCount} />
												</Inline>
											</Box>
										)}
									/>
								) : (
									<Tooltip
										content={
											<Stack space="space.100">
												{(scopes ?? []).map((scope, index) => (
													<Box key={`${scope.link ?? 'scope'}-${index}`}>
														<ScopeRow
															scope={scope}
															variant="tooltip"
															maxLines={2}
															onFilePreview={(link, name) => setFileScopePreview({ link, name })}
															onScopeLinkClick={handleScopeLinkClick}
														/>
													</Box>
												))}
											</Stack>
										}
										delay={0}
										position="bottom"
										tag="span"
									>
										{(tooltipProps) => (
											<Box as="span" xcss={styles.firstScopeTooltipTrigger} {...tooltipProps}>
												<Inline alignBlock="center" space="space.050">
													{visibleScopes.map((scope, index) => (
														<Box
															key={`${scope.link ?? 'scope'}-${index}`}
															xcss={styles.scopeNameWrapper}
														>
															<ScopeRow
																scope={{ ...scope, link: undefined }}
																variant="default"
																maxLines={1}
																showName={!hasMultipleScopes}
																showLinkFallbackIcon
																showIconWithoutLink
																onScopeLinkHover={handleScopeLinkHovered}
															/>
														</Box>
													))}
													<ScopeOverflowCount count={scopesOverflowCount} />
												</Inline>
											</Box>
										)}
									</Tooltip>
								)}
							</React.Fragment>
						)}
						{!showThinkingFooterRow && isPinnedChatsEnabled && conversation.isPinned && (
							<React.Fragment>
								{(conversation.lastMessageTimestamp ||
									(shouldShowAgentInfo && conversation.agent) ||
									firstScope) && <FooterSeparator color={footerTextColor} />}
								<Box xcss={styles.pinnedLabel}>
									<Text size="small" weight="medium" color="color.text.accent.blue">
										{formatMessage(messages.chatPinnedStatusLabel)}
									</Text>
								</Box>
							</React.Fragment>
						)}
					</Inline>
				</Box>
			</Inline>

			{/* Context menu / status — share the same right-side slot.
			     The dropdown is always rendered (opacity:0) to preserve open state.
			     Status and dropdown are co-located in the same Box so they overlay
			     each other without affecting layout. */}
			{!hideActions && (
				<Box xcss={cx(styles.contextMenuContainer, styles.controlsContainer)}>
					{!showControls && (
						<Box xcss={styles.statusContainer}>
							<ConversationListRowStatusChip
								conversation={conversation}
								testId={`chat-conversation-list-refresh-row-status-${conversation.id}`}
							/>
						</Box>
					)}

					<Box xcss={cx(!showControls && styles.hidden)}>
						<DropdownMenu<HTMLButtonElement>
							shouldRenderToParent
							onOpenChange={handleContextMenuToggle}
							trigger={({ triggerRef, onClick, ...rest }) => (
								<IconButton
									{...rest}
									label={formatMessage(messages.chatOptionsForConversationButtonLabel, {
										conversationName: conversation.name,
									})}
									appearance="default"
									icon={ShowMoreHorizontalIcon}
									ref={triggerRef}
									onClick={(e, analyticsEvent) => {
										sendAnalyticsEvent(
											analyticsEvent.update({
												actionSubjectId: 'chatViewChatOptionsTrigger',
											}),
										);
										e.stopPropagation();

										onClick?.(e);
									}}
								/>
							)}
						>
							<DropdownItemGroup>
								<DropdownItem
									onClick={(e) => {
										e.stopPropagation();

										sendAnalyticsEvent({
											action: 'clicked',
											actionSubject: 'button',
											actionSubjectId: 'renameConversationButton',
										});

										openModal(conversation, 'edit');
									}}
								>
									{formatMessage(messages.chatRenameLabel)}
								</DropdownItem>
								{onConversationPinToggle && (
									<DropdownItem onClick={handlePinMenuItemClicked}>{pinButtonLabel}</DropdownItem>
								)}
								<DropdownItem
									onClick={(e) => {
										e.stopPropagation();

										sendAnalyticsEvent({
											action: 'clicked',
											actionSubject: 'button',
											actionSubjectId: 'deleteConversationButton',
										});

										openModal(conversation, 'delete');
									}}
								>
									{formatMessage(messages.chatDeleteLabel)}
								</DropdownItem>
							</DropdownItemGroup>
						</DropdownMenu>
					</Box>
				</Box>
			)}

			{/* File preview modal */}
			{fileScopePreview ? (
				<ScopeFileBlobPreviewModal
					isOpen
					onClose={() => setFileScopePreview(null)}
					fileUrl={fileScopePreview.link}
					fileName={fileScopePreview.name}
					formatMessage={formatMessage}
					filePreviewFallbackDescription={messages.filePreviewFallbackDescription}
					cloudId={cloudId}
				/>
			) : null}
		</Box>
	);
};
